/**
 * OpLog — undo/redo deque for IR ops.
 *
 * Design: docs/design/spa-a11y-editor-phase3-power-editor-260430.md §3.3
 *
 * Each op carries `before` and `after`. Inverse op is computed by swapping
 * before/after. Capped at 1000 transactions to cap memory; oldest entries
 * drop off the front when full.
 *
 * DRY (Epic pdf-editor-shared-a11y-view-dry-260810 Phase 3, C.2): the a11y op
 * kinds shared with the format-agnostic view substrate delegate their inverse to
 * the SINGLE shared `inverseViewOp` (a11y-view/view-concerns) — the swap logic is
 * IR-shape-independent, so there is no PDF-side duplicate (A.7, no shim). The
 * PDF-native op kinds (set-role / set-attestation / set-actual-text / set-bbox /
 * create-element) keep their inverse here.
 *
 * Usage:
 *   const log = new OpLog();
 *   log.push(op);          // after a successful applyOp
 *   if (log.canUndo()) {
 *       const inverse = log.undo();    // returns inverse op or null
 *       await applyOp(inverse);
 *   }
 *   if (log.canRedo()) {
 *       const reapply = log.redo();    // returns the original forward op
 *       await applyOp(reapply);
 *   }
 *
 * Undo/redo operates on single ops (not transaction batches). Transaction
 * grouping is a future addition for multi-op edits like split-paragraph.
 */
// OpKinds — generated discriminator-literal const table sourced from
// system-models/wire-contracts/editor-edit-op.schema.json (Phase 0 of
// docs/design/wire-contracts-migration-project.md). The case
// labels below cite OpKinds for the wire-bound kinds so a schema edit
// surfaces as a JS reference error rather than a silent typo.
import { OpKinds } from "./wire-contracts/editor-edit-op.js";
import { inverseViewOp } from "../a11y-view/view-concerns.js";
const DEFAULT_CAP = 1000;
/**
 * The a11y op kinds whose inverse is a pure, IR-shape-INDEPENDENT before/after
 * swap AND which are part of the shared format-agnostic view vocabulary
 * (`ViewOpKind` in a11y-view/view-types) — the PDF editor's slice of it, matching
 * `PDF_OP_VOCABULARY` (pdf-view-adapter.ts). Their inverse is owned ONCE by the
 * shared `inverseViewOp`; this set routes them there (C.2 unify, no PDF-side
 * duplicate). `set-long-desc` is in the shared union but the PDF editor never
 * emits it, so it is absent here.
 *
 * Module-private: oplog.ts's public surface is pinned to exactly
 * `{ OpLog, inverseOf }` (oplog.seam.test.ts) — do NOT export this.
 */
const SHARED_A11Y_INVERSE_KINDS = new Set([
    OpKinds.SET_ALT_TEXT,
    "set-decorative",
    OpKinds.SET_TITLE,
    OpKinds.SET_LANG,
    OpKinds.REORDER_READING,
]);
export function inverseOf(op) {
    if (!op || typeof op.kind !== "string") {
        throw new TypeError("inverseOf: op missing 'kind'");
    }
    // Shared a11y vocabulary → the ONE shared inverse. `inverseViewOp` is total
    // over the a11y union (never null) and spreads the op (`{ ...op, ... }`), so
    // PDF-carried extras like `structId` survive exactly as the former inline swap
    // preserved them. The IrOp↔ViewOp cast bridges the loose editor op shape to the
    // typed shared union at this boundary (the kind-set above is the runtime proof
    // the shapes line up).
    if (SHARED_A11Y_INVERSE_KINDS.has(op.kind)) {
        return inverseViewOp(op);
    }
    switch (op.kind) {
        case OpKinds.SET_ROLE:
        case "set-attestation":
            // PDF-native kinds not in the shared a11y vocabulary; the inverse is
            // still a plain before/after swap, but the op shapes are PDF-specific.
            return { ...op, before: op.after, after: op.before };
        case OpKinds.SET_ACTUAL_TEXT:
            // set-actual-text in our schema only has `after` (per ir.js
            // typedef). Since undo needs a `before`, we require the
            // caller to attach a `before` field at push() time. If it's
            // absent, undo cannot reconstruct the prior state — return
            // null to signal that.
            if (typeof op.before !== "string")
                return null;
            return { ...op, before: op.after, after: op.before };
        case OpKinds.SET_BBOX:
            // before/after are [x, y, w, h] arrays; swap to invert.
            return { ...op, before: op.after, after: op.before };
        case "create-element":
            // The inverse of create-element is delete-element (remove the
            // block by structId). The IR reducer's `delete-element` case
            // pairs with create-element so the round-trip is symmetric.
            return { kind: "delete-element", structId: op.structId };
        case "delete-element": {
            // Item-delete undo (editor-pdf-item-editing-260910 Phase 4b): a
            // delete that removed an LI from a `List` (carries parentStructId)
            // inverts to a `create-element` re-inserting the LI at its former
            // index — the reducer's stated undo contract (structure-reducers.ts
            // §deleteElementReducer: "undo … built by the caller as an inverse
            // create-element"). insertListItem re-materializes the LI at
            // `insertIndex` with its inner Paragraph carrying the LI structId,
            // so a subsequent set-actual-text round-trips. A top-level block
            // delete carries NO parentStructId; its inverse stays null (those
            // undo via the `state.undoRedo` splice-in stack, which also
            // restores content + position) — preserving the historical shape.
            if (typeof op.parentStructId !== "string" || op.parentStructId === "")
                return null;
            if (typeof op.structId !== "string" || op.structId === "")
                return null;
            const inv = {
                kind: "create-element",
                structId: op.structId,
                parentStructId: op.parentStructId,
                role: "LI",
                bbox: [0, 0, 0, 0],
                page: typeof op.page === "number" ? op.page : 1,
            };
            if (typeof op.insertIndex === "number")
                inv["insertIndex"] = op.insertIndex;
            return inv;
        }
        default:
            return null;
    }
}
/**
 * Bounded deque of ops with undo/redo cursors.
 *
 * The deque holds entries in *applied* order (oldest first). The
 * `cursor` points to the next slot to fill; ops at indices >= cursor
 * are redo-able (they were undone but not yet redone).
 *
 * push() truncates the redo tail; this matches every editor's "do
 * something new and lose the redo stack" behaviour.
 */
export class OpLog {
    _cap;
    _entries;
    _cursor;
    constructor(opts = {}) {
        this._cap = opts.cap ?? DEFAULT_CAP;
        this._entries = [];
        this._cursor = 0;
    }
    push(op) {
        if (!op || typeof op.kind !== "string") {
            throw new TypeError("OpLog.push: op missing 'kind'");
        }
        // Drop redo tail.
        if (this._cursor < this._entries.length) {
            this._entries.length = this._cursor;
        }
        this._entries.push(op);
        // Cap.
        if (this._entries.length > this._cap) {
            this._entries.shift();
        }
        else {
            this._cursor = this._entries.length;
        }
        if (this._entries.length === this._cap) {
            // After shift, cursor must equal length.
            this._cursor = this._entries.length;
        }
    }
    undo() {
        if (this._cursor === 0)
            return null;
        const op = this._entries[this._cursor - 1]; // WHY: this._cursor > 0 check above, so cursor-1 >= 0
        const inv = inverseOf(op);
        if (inv === null)
            return null;
        this._cursor -= 1;
        return inv;
    }
    redo() {
        if (this._cursor >= this._entries.length)
            return null;
        const op = this._entries[this._cursor]; // WHY: this._cursor < this._entries.length check above
        this._cursor += 1;
        return op;
    }
    canUndo() { return this._cursor > 0; }
    canRedo() { return this._cursor < this._entries.length; }
    /** Number of entries currently held (including undone-but-redoable). */
    size() { return this._entries.length; }
    /** For debugging / tests. */
    snapshot() {
        return { entries: [...this._entries], cursor: this._cursor };
    }
}
